'use client';

import { JsonLd } from '@/seo/JsonLd';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';

type BlogPost = {
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author: {
    name: string;
    url?: string;
    image?: string;
  };
  image: string[];
  keywords?: string[];
  articleSection?: string;
  about?: Array<{ '@type': string; name: string }>;
  faqs?: Array<{ question: string; answer: string }>;
  howToSteps?: Array<{ name: string; text: string; image?: string }>;
  wordCount?: number;
  commentCount?: number;
  articleBody?: string;
  mentions?: Array<{ '@type': string; name: string }>;
  isPartOf?: { '@type': string; name: string; url: string };
  relatedProducts?: Array<{
    slug: string;
    name: string;
    description: string;
    image: string;
    sku: string;
    mpn: string;
    priceTRY?: number;
    priceUnit?: 'm2' | 'plaka' | 'adet';
    reviews?: Array<{
      authorName: string;
      datePublished: string;
      ratingValue: number;
      title: string;
      body: string;
      verified?: boolean;
    }>;
  }>;
};

type Props = {
  post: BlogPost;
  pathname: string;
  breadcrumb?: Array<{ name: string; item: string }>;
};

export function BlogSEO({ post, pathname, breadcrumb }: Props) {
  const url = toAbsoluteUrl(pathname);
  const publishedDate = new Date(post.datePublished).toISOString();
  const modifiedDate = post.dateModified ? new Date(post.dateModified).toISOString() : publishedDate;

  const schemaGraph: any[] = [
    {
      '@type': 'WebSite',
      '@id': `${toAbsoluteUrl('/')}#website`,
      name: 'Bondex Sünger',
      url: toAbsoluteUrl('/'),
      inLanguage: 'tr-TR',
      publisher: { '@id': `${toAbsoluteUrl('/')}#organization` },
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${toAbsoluteUrl('/')}?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Organization',
      '@id': `${toAbsoluteUrl('/')}#organization`,
      name: 'bariyerlibondexsunger.com',
      url: toAbsoluteUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl('/images/bondex-logo.webp'),
        width: 512,
        height: 512,
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+90 216 519 34 11',
          contactType: 'customer service',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
        {
          '@type': 'ContactPoint',
          telephone: '+90 546 919 11 34',
          contactType: 'sales',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
      ],
      sameAs: [
        'https://www.facebook.com/profile.php?id=61575206163572',
        'https://x.com/KabinPods',
        'https://www.instagram.com/kabinpods/',
        'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
        'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
      ],
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00',
        },
      ],
    },
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: post.headline,
      description: post.description,
      isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
      inLanguage: 'tr-TR',
      datePublished: publishedDate,
      dateModified: modifiedDate,
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl(post.image[0]),
        width: 1200,
        height: 630,
      },
      breadcrumb: breadcrumb ? { '@id': `${url}#breadcrumb` } : undefined,
      mainEntity: { '@id': `${url}#blogposting` },
      about: post.about || [
        { '@type': 'Thing', name: 'Bondex sünger' },
        { '@type': 'Thing', name: 'ses yalıtım süngeri' },
      ],
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', 'h2', 'p'],
      },
      publisher: { '@id': `${toAbsoluteUrl('/')}#organization` },
    },
  ];

  // BreadcrumbList schema
  if (breadcrumb && breadcrumb.length > 0) {
    schemaGraph.push({
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: breadcrumb.map((b, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: b.name,
        item: b.item,
      })),
    });
  }

  // BlogPosting schema (main content)
  const blogPosting: any = {
    '@type': 'BlogPosting',
    '@id': `${url}#blogposting`,
    mainEntityOfPage: url,
    headline: post.headline,
    description: post.description,
    datePublished: publishedDate,
    dateModified: modifiedDate,
    author: {
      '@type': 'Person',
      name: post.author.name,
      ...(post.author.url && { url: post.author.url }),
      ...(post.author.image && {
        image: {
          '@type': 'ImageObject',
          url: toAbsoluteUrl(post.author.image),
        },
      }),
    },
    publisher: {
      '@type': 'Organization',
      name: 'bariyerlibondexsunger.com',
      url: toAbsoluteUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl('/images/bondex-logo.webp'),
        width: 512,
        height: 512,
      },
      sameAs: [
        'https://www.facebook.com/profile.php?id=61575206163572',
        'https://x.com/KabinPods',
        'https://www.instagram.com/kabinpods/',
        'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
        'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
      ],
    },
    image: post.image.map((img) => toAbsoluteUrl(img)),
    inLanguage: 'tr-TR',
  };

  // Add optional fields
  if (post.keywords && post.keywords.length > 0) {
    blogPosting.keywords = post.keywords.join(', ');
  }

  if (post.articleSection) {
    blogPosting.articleSection = post.articleSection;
  }

  if (post.about && post.about.length > 0) {
    blogPosting.about = post.about;
  }

  if (post.wordCount) {
    blogPosting.wordCount = post.wordCount;
  }

  if (post.commentCount !== undefined) {
    blogPosting.commentCount = post.commentCount;
  }

  if (post.articleBody) {
    blogPosting.articleBody = post.articleBody;
  }

  if (post.mentions && post.mentions.length > 0) {
    blogPosting.mentions = post.mentions;
  }

  if (post.isPartOf) {
    blogPosting.isPartOf = {
      '@type': post.isPartOf['@type'],
      name: post.isPartOf.name,
      url: post.isPartOf.url,
    };
  }

  schemaGraph.push(blogPosting);

  // FAQPage schema if FAQs exist
  if (post.faqs && post.faqs.length > 0) {
    schemaGraph.push({
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: post.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  // HowTo schema if steps exist
  if (post.howToSteps && post.howToSteps.length > 0) {
    const howToSteps = post.howToSteps.map((step, index) => {
      const stepObj: any = {
        '@type': 'HowToStep',
        position: index + 1,
        name: step.name,
        text: step.text,
      };
      if (step.image) {
        stepObj.image = toAbsoluteUrl(step.image);
      }
      return stepObj;
    });

    schemaGraph.push({
      '@type': 'HowTo',
      '@id': `${url}#howto`,
      name: post.headline,
      description: post.description,
      step: howToSteps,
      totalTime: 'PT30M', // Estimated time (optional)
    });
  }

  // Product snippets and Merchant listings - Related products
  if (post.relatedProducts && post.relatedProducts.length > 0) {
    const validFromStr = new Date().toISOString().split('T')[0];
    const priceValidUntil = new Date();
    priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
    const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

    // Add MerchantReturnPolicy schema FIRST (before Offer references it)
    schemaGraph.push({
      '@type': 'MerchantReturnPolicy',
      '@id': `${url}#merchantReturnPolicy`,
      applicableCountry: {
        '@type': 'Country',
        name: 'TR',
      },
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    });

    // Add OfferShippingDetails schema FIRST (before Offer references it)
    schemaGraph.push({
      '@type': 'OfferShippingDetails',
      '@id': `${url}#shippingDetails`,
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: '0',
        currency: 'TRY',
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: {
          '@type': 'Country',
          name: 'TR',
        },
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 3,
          unitCode: 'DAY',
        },
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 5,
          unitCode: 'DAY',
        },
        businessDays: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        },
        cutoffTime: '14:00',
      },
    });

    post.relatedProducts.forEach((product, index) => {
      const productUrl = toAbsoluteUrl(`/${product.slug}`);
      const productId = `${url}#product-${index + 1}`;

      // Product schema
      const productSchema: any = {
        '@type': 'Product',
        '@id': productId,
        name: product.name,
        description: product.description,
        image: [toAbsoluteUrl(product.image)],
        sku: product.sku,
        mpn: product.mpn,
        brand: {
          '@type': 'Brand',
          name: 'Bondex',
        },
        url: productUrl,
        category: post.articleSection || 'Ses Yalıtımı',
      };

      // Add Offer (Merchant listings)
      if (product.priceTRY) {
        const offer: any = {
          '@type': 'Offer',
          '@id': `${productId}#offer`,
          url: productUrl,
          priceCurrency: 'TRY',
          price: String(product.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: {
            '@id': `${toAbsoluteUrl('/')}#organization`,
          },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        };

        if (product.priceUnit === 'm2') {
          offer.unitText = 'm²';
          offer.priceSpecification = {
            '@type': 'UnitPriceSpecification',
            price: String(product.priceTRY),
            priceCurrency: 'TRY',
            unitCode: 'MTK',
            unitText: 'm²',
          };
        }

        productSchema.offers = offer;
      }

      // Add AggregateRating and Review (Review snippets)
      if (product.reviews && product.reviews.length > 0) {
        const reviewCount = product.reviews.length;
        const ratingValue =
          product.reviews.reduce((sum, r) => sum + (Number.isFinite(r.ratingValue) ? r.ratingValue : 0), 0) / reviewCount;

        productSchema.aggregateRating = {
          '@type': 'AggregateRating',
          '@id': `${productId}#aggregateRating`,
          ratingValue: ratingValue.toFixed(1),
          reviewCount,
          bestRating: '5',
          worstRating: '1',
        };

        productSchema.review = product.reviews.map((r, reviewIndex) => ({
          '@type': 'Review',
          '@id': `${productId}#review-${reviewIndex + 1}`,
          author: {
            '@type': 'Person',
            name: r.authorName,
          },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: {
            '@type': 'Rating',
            ratingValue: String(r.ratingValue),
            bestRating: '5',
            worstRating: '1',
          },
        }));
      }

      schemaGraph.push(productSchema);
    });
  }

  const schema = {
    '@context': 'https://schema.org',
    '@graph': schemaGraph,
  };

  return (
    <JsonLd data={schema} />
  );
}

